import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import { fetchCourseCertificateUrl } from 'actionCreators/course';
import ModuleList from 'components/Lesson/Modules';
import { ICourse, IModule } from 'interface/course';
import { Fragment, MouseEventHandler, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { ILesson } from 'interface/lesson';
import { makeStyles } from '@material-ui/core/styles';
import ChevronLeft from '@eduzz/houston-icons/ChevronLeft';
import ChevronRight from '@eduzz/houston-icons/ChevronRight';
import Progress from 'components/Course/Informations/Progress';
import { Button, Typography } from '@material-ui/core';

interface IProps {
  courseHash: string;
  course: ICourse;
  modules: IModule[];
  lesson: ILesson;
  fetchCourseCertificateUrl: Function;
  open?: boolean;
  mobileOpen?: boolean;
  onClose?: MouseEventHandler<HTMLDivElement>;
  onCloseMobile?: MouseEventHandler<HTMLDivElement> | any;
  onOpen?: MouseEventHandler<HTMLDivElement>;
  onOpenMobile?: MouseEventHandler<HTMLDivElement>;
  onToggle?: MouseEventHandler<HTMLDivElement>;
  authenticated: boolean;
}

const useStyles = makeStyles((theme) => {
  return {
    drawerPaper: {
      transition: 'all 0.3s ease-in-out',
      overflow: 'hidden',
      marginTop: 56,
      width: 360,
      height: 'calc(100vh - 106px)',
      backgroundColor: theme.palette.grey['500'],
      zIndex: 1,
      borderRight: 'none',
      display: 'flex',
      flexDirection: 'column',
      '@media(max-width: 600px)': {
        width: '100%',
      },
      '&.mobile': {
        marginTop: 0,
        height: '100vh',
      },
    },
    onCloseMenu: {
      '& > div': {
        transform: 'translateX(-381px)',
        transition: 'all 0.3s ease-in-out',
      },
    },
    sideMenuRoot: {
      position: 'fixed',
    },
    iconToggle: {
      transition: 'all 0.3s ease-in-out',
      cursor: 'pointer',
      '& > span > svg': {
        fill: theme.palette.common.white,
      },
      '&.openSideMenu': {
        position: 'fixed',
        width: 30,
        display: 'flex',
        alignItems: 'center',
        top: 94,
        height: 'calc(100vh - 144px)',
        backgroundColor: 'transparent',
        backgroundImage: `linear-gradient(90deg, ${theme.palette.grey['600']},transparent 90%)`,
        zIndex: 10,
      },
    },
    content: {
      padding: theme.spacing(4),
      background: theme.palette.grey['500'],
      '@media(min-width: 960px)': {
        marginTop: 40,
      },
    },
    container: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    titleAndClose: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    courseTitle: {
      color: theme.palette.common.white,
      fontWeight: 700,
      display: '-webkit-box',
      textOverflow: 'ellipsis',
      wordBreak: 'break-word',
      overflow: 'hidden',
      boxOrient: 'vertical',
      lineClamp: 3,
      width: '100%',
    },
    courseProgress: {
      '& > label': {
        color: theme.palette.common.white,
        fontWight: 700,
        lineHeight: '16px',
        fontSize: 10,
      },
    },
    progressBar: {
      background: theme.palette.grey['600'],
      borderRadius: 8,
      width: 106,
      '& > div': {
        background: theme.palette.primary.light,
        height: 4,
        borderRadius: 8,
      },
    },
    category: {
      margin: theme.spacing(2, 0),
      color: theme.palette.primary.light,
    },
    certificateButton: {
      marginLeft: theme.spacing(-4),
      marginBottom: theme.spacing(-4),
      marginTop: theme.spacing(3),
      borderRadius: 0,
      color: theme.palette.common.white,
      background: theme.palette.primary.main,
      height: 60,
      width: 'calc(100% + 32px)',
      '&:hover': {
        background: theme.palette.primary.dark,
      },
    },
  };
});

const SideMenu = (props: IProps) => {
  const classes = useStyles();

  useEffect(() => {
    if (props.onCloseMobile) {
      props.onCloseMobile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.lesson?.id]);

  const renderDrawer = () => (
    <Fragment>
      <div className={classes.content}>
        {props.course.title && (
          <Fragment>
            <div className={classes.titleAndClose}>
              <Link className='link-title' to={`/curso/${props.courseHash}`}>
                <Typography
                  variant='body1'
                  aria-label='Título do curso'
                  className={classes.courseTitle}
                  id='lesson-course-title'
                >
                  {props.course.title}
                </Typography>
              </Link>
              <Hidden smDown>
                <div
                  aria-label='Botão fechar menu lateral'
                  className={classes.iconToggle}
                  onClick={props.onToggle}
                  id='lesson-collapse-menu'
                >
                  <ChevronLeft size={26} />
                </div>
              </Hidden>
              <Hidden mdUp>
                <div className={classes.iconToggle} onClick={props.onCloseMobile}>
                  <ChevronLeft size={26} />
                </div>
              </Hidden>
            </div>
            <div aria-label='Nome da categoria do curso' className={classes.category} id='lesson-course-category'>
              {props.course.category.name}
            </div>
            <Progress size='small'>
              <Button
                data-label='Botão visualizar certificado'
                className={classes.certificateButton}
                variant='contained'
                color='secondary'
              >
                Imprimir Certificado
              </Button>
            </Progress>
          </Fragment>
        )}
      </div>
      <ModuleList onCloseMobile={props.onCloseMobile} />
    </Fragment>
  );

  return (
    <Fragment>
      <Hidden smDown implementation='css'>
        {!props.open && (
          <Hidden smDown>
            <div className={`${classes.iconToggle} openSideMenu`} onClick={props.onToggle} id='lesson-expand-menu'>
              <ChevronRight />
            </div>
          </Hidden>
        )}
        <Drawer
          className={`${classes.sideMenuRoot} ${!props.open && classes.onCloseMenu}`}
          variant='permanent'
          classes={{ paper: classes.drawerPaper }}
        >
          {renderDrawer()}
        </Drawer>
      </Hidden>
      <Hidden mdUp>
        <SwipeableDrawer
          className={classes.sideMenuRoot}
          variant='temporary'
          classes={{ paper: `${classes.drawerPaper} mobile` }}
          ModalProps={{ keepMounted: true }}
          open={props.mobileOpen}
          onClose={props.onCloseMobile}
          onOpen={props.onOpenMobile}
        >
          {renderDrawer()}
        </SwipeableDrawer>
      </Hidden>
    </Fragment>
  );
};

const mapStateToProps = (state: any) => ({
  lesson: state.newLesson.lesson,
  course: state.course,
  modules: state.modules,
  authenticated: state.auth.isAuthenticated,
});

export default connect(mapStateToProps, { fetchCourseCertificateUrl })(SideMenu);
